/**
 * Own Invitation Actions
 *
 * Shared lifecycle helpers for current-user invitation accept and
 * decline actions.
 *
 * Primary responsibilities:
 * - Define stable success and error payloads
 * - Map invitation lifecycle states to action results
 * - Parse action responses safely on the client
 *
 * Dependencies:
 * - None
 */

export type OwnInvitationLifecycleStatus =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'cancelled'

export type OwnInvitationActionSuccessStatus =
  | 'accepted'
  | 'already_accepted'
  | 'declined'
  | 'already_declined'

export type OwnInvitationActionErrorCode =
  | 'auth_required'
  | 'user_email_missing'
  | 'invitation_not_found'
  | 'invitation_wrong_account'
  | 'invitation_expired'
  | 'invitation_cancelled'
  | 'invitation_declined'
  | 'invitation_already_accepted'
  | 'invitation_invalid_role'
  | 'workspace_not_found'
  | 'space_not_found'
  | 'item_not_found'
  | 'invitation_state_conflict'
  | 'invitation_failed'

export type OwnInvitationActionSuccess = {
  ok: true
  status: OwnInvitationActionSuccessStatus
  message: string
  workspaceId?: string
  spaceId?: string
  itemId?: string
  itemType?: string
}

export type OwnInvitationActionFailure = {
  ok: false
  code: OwnInvitationActionErrorCode
  message: string
}

export type OwnInvitationActionResult =
  | OwnInvitationActionSuccess
  | OwnInvitationActionFailure

const OWN_INVITATION_STATUSES: OwnInvitationLifecycleStatus[] = [
  'pending',
  'accepted',
  'declined',
  'expired',
  'cancelled'
]

const SUCCESS_STATUSES: OwnInvitationActionSuccessStatus[] = [
  'accepted',
  'already_accepted',
  'declined',
  'already_declined'
]

const ERROR_CODES: OwnInvitationActionErrorCode[] = [
  'auth_required',
  'user_email_missing',
  'invitation_not_found',
  'invitation_wrong_account',
  'invitation_expired',
  'invitation_cancelled',
  'invitation_declined',
  'invitation_already_accepted',
  'invitation_invalid_role',
  'workspace_not_found',
  'space_not_found',
  'item_not_found',
  'invitation_state_conflict',
  'invitation_failed'
]

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const asString = (value: unknown): string | null => {
  return typeof value === 'string' ? value : null
}

export const isOwnInvitationLifecycleStatus = (
  value: string
): value is OwnInvitationLifecycleStatus => {
  return OWN_INVITATION_STATUSES.includes(value as OwnInvitationLifecycleStatus)
}

export const isInvitationExpired = (expiresAt: string): boolean => {
  const expiresAtMs = Date.parse(expiresAt)

  if (!Number.isFinite(expiresAtMs)) {
    return false
  }

  return expiresAtMs <= Date.now()
}

export const ownInvitationSuccess = (
  status: OwnInvitationActionSuccessStatus,
  message: string,
  context?: {
    workspaceId?: string | null
    spaceId?: string | null
    itemId?: string | null
    itemType?: string | null
  }
): OwnInvitationActionSuccess => ({
  ok: true,
  status,
  message,
  workspaceId: context?.workspaceId ?? undefined,
  spaceId: context?.spaceId ?? undefined,
  itemId: context?.itemId ?? undefined,
  itemType: context?.itemType ?? undefined
})

export const ownInvitationFailure = (
  code: OwnInvitationActionErrorCode,
  message: string
): OwnInvitationActionFailure => ({
  ok: false,
  code,
  message
})

export const resolveOwnInvitationAcceptState = (
  status: OwnInvitationLifecycleStatus
): OwnInvitationActionResult | null => {
  switch (status) {
    case 'pending':
      return null
    case 'accepted':
      return ownInvitationSuccess('already_accepted', 'Invitation already accepted')
    case 'declined':
      return ownInvitationFailure(
        'invitation_declined',
        'Invitation has already been declined'
      )
    case 'expired':
      return ownInvitationFailure('invitation_expired', 'Invitation has expired')
    case 'cancelled':
      return ownInvitationFailure(
        'invitation_cancelled',
        'Invitation has been cancelled'
      )
  }
}

export const resolveOwnInvitationDeclineState = (
  status: OwnInvitationLifecycleStatus
): OwnInvitationActionResult | null => {
  switch (status) {
    case 'pending':
      return null
    case 'accepted':
      return ownInvitationFailure(
        'invitation_already_accepted',
        'Invitation was already accepted'
      )
    case 'declined':
      return ownInvitationSuccess('already_declined', 'Invitation already declined')
    case 'expired':
      return ownInvitationFailure('invitation_expired', 'Invitation has expired')
    case 'cancelled':
      return ownInvitationFailure(
        'invitation_cancelled',
        'Invitation has been cancelled'
      )
  }
}

export const getOwnInvitationErrorStatus = (
  code: OwnInvitationActionErrorCode
): number => {
  switch (code) {
    case 'auth_required':
      return 401
    case 'invitation_wrong_account':
      return 403
    case 'invitation_not_found':
    case 'workspace_not_found':
    case 'space_not_found':
    case 'item_not_found':
      return 404
    case 'user_email_missing':
    case 'invitation_invalid_role':
      return 400
    case 'invitation_expired':
      return 410
    case 'invitation_cancelled':
    case 'invitation_declined':
    case 'invitation_already_accepted':
    case 'invitation_state_conflict':
      return 409
    case 'invitation_failed':
      return 500
  }
}

export const readOwnInvitationActionResult = (
  value: unknown
): OwnInvitationActionResult | null => {
  if (!isRecord(value) || typeof value.ok !== 'boolean') {
    return null
  }

  const message = asString(value.message)

  if (!message) {
    return null
  }

  if (value.ok) {
    const status = asString(value.status)

    if (!status || !SUCCESS_STATUSES.includes(status as OwnInvitationActionSuccessStatus)) {
      return null
    }

    return {
      ok: true,
      status,
      message,
      workspaceId: asString(value.workspaceId) || undefined,
      spaceId: asString(value.spaceId) || undefined
    }
  }

  const code = asString(value.code)

  if (!code || !ERROR_CODES.includes(code as OwnInvitationActionErrorCode)) {
    return null
  }

  return {
    ok: false,
    code,
    message
  }
}

export class OwnInvitationActionError extends Error {
  code: OwnInvitationActionErrorCode

  constructor(code: OwnInvitationActionErrorCode, message: string) {
    super(message)
    this.name = 'OwnInvitationActionError'
    this.code = code
  }
}

export const isOwnInvitationActionError = (
  error: unknown
): error is OwnInvitationActionError => {
  return error instanceof OwnInvitationActionError
}
